import { useMemo } from 'react'

import { useReactiveVar } from '@apollo/client'
import { useSelector } from 'react-redux'

import { OrderStatus } from '__generated__/atlas-types'

import {
  isAccountTypeBrand,
  isEditApprovedShippedRestricted,
} from 'store/currentUser/selectors'

import { orderReviewPartialErrorVar } from '../../review.store'
import { OrderData } from '../Overview/Overview'
import { useHasUnavailableSkus } from '../Overview/components/ProductsWidget/hooks/useHasUnavailableSkus'

export const useOrderButtonState = ({
  order,
}: {
  order: {
    status: OrderData['status']
    collections?: OrderData['collections']['edges']
    products?: { collection: [] }[]
  }
}) => {
  const isBrandAccount = useSelector(isAccountTypeBrand)
  const isRestrictEditApprovedShipped = useSelector(
    isEditApprovedShippedRestricted,
  )
  const hasUnavailableSkus = useHasUnavailableSkus()
  const isTherePriceTypeError = useReactiveVar(
    orderReviewPartialErrorVar,
  )?.priceType
  return useMemo(() => {
    let errors = []
    if (isTherePriceTypeError) {
      errors.push({
        isButtonHidden: false,
        reason:
          'An update to the price type associated with this order has caused cost information to be out of date. Reach out to the brand directly if you believe this is a mistake.',
      })
    }
    if (hasUnavailableSkus) {
      errors.push({
        isButtonHidden: false,
        reason:
          'Updates to styles in this order have caused quantity and cost information to be out of date. Hit edit to see the styles that have been affected.',
      })
    }
    if (isBrandAccount) {
      if (
        isRestrictEditApprovedShipped &&
        order?.status === OrderStatus.SHIPPED
      ) {
        errors.push({
          isButtonHidden: true,
          reason: 'Products cannot be added or edited in Shipped orders.',
        })
      }

      if (
        isRestrictEditApprovedShipped &&
        order?.status === OrderStatus.APPROVED
      ) {
        errors.push({
          isButtonDisabled: true,
          reason:
            'You are restricted from adding or editing products in Approved orders.  Contact your account admin for assistance.',
        })
      }

      if (order?.status === OrderStatus.CANCELLED) {
        errors.push({
          isButtonHidden: true,
          reason: 'Products cannot be added to or edited in Cancelled orders.',
        })
      }

      return errors
    }

    if (order?.collections?.length === 0) {
      errors.push({
        isButtonDisabled: true,
        reason: `The brand selected has no available linesheets to add products from. Contact the brand for assistance.`,
      })
    }

    const allCollectionsArchivedOrHidden = order?.collections?.every(
      (collection) =>
        collection?.node.archived || !collection?.node.visibleToViewer,
    )

    if (allCollectionsArchivedOrHidden) {
      errors.push({
        isButtonDisabled: true,
        reason:
          'The brand selected has no shared linesheets for you to add products from. Contact the brand for assistance.',
      })
    }

    if (order?.status === OrderStatus.APPROVED) {
      errors.push({
        isButtonDisabled: true,
        reason: `You are restricted from adding or editing products in Approved orders. Contact the brand for assistance.`,
      })
    }

    if (order?.status === OrderStatus.SHIPPED) {
      errors.push({
        isButtonHidden: true,
        reason: `Products cannot be added or edited in Shipped orders.`,
      })
    }

    if (order?.status === OrderStatus.PENDING) {
      errors.push({
        isButtonDisabled: true,
        reason: `Products cannot be added or edited in Pending orders. Click the "Edit Order" button to allow changes.`,
      })
    }

    if (order?.status === OrderStatus.CANCELLED) {
      errors.push({
        isButtonHidden: true,
        reason: `Products cannot be added to or edited in Cancelled orders.`,
      })
    }

    if (order?.products?.some((prod) => !prod.collection)) {
      errors.push({
        isButtonHidden: false,
        reason:
          'The linesheet associated with some or all of the products below is no longer visible. Reach out to the brand directly if you believe this is a mistake.',
      })
    }

    return errors
  }, [
    isBrandAccount,
    isRestrictEditApprovedShipped,
    order?.status,
    order?.collections,
  ])
}
